import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  api,
  getApiErrorCode,
  getApiErrorMessage,
  isEmailVerificationRequiredError,
} from '@/services/api';
import { featureFlags } from '@/config/featureFlags';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  Copy,
  Download,
  FileText,
  Info,
  Loader2,
  RefreshCw,
  Sparkles,
  Upload,
} from 'lucide-react';
import MainMenu from '@/components/MainMenu';
import {
  buildGuidedResumeExportPayload,
  getGuidedResumeResultView,
} from '@/lib/guidedResumeResult';
import {
  getUnsupportedUploadFormatMessage,
  isSupportedUploadFile,
  LEGACY_DOC_GUIDANCE,
  SUPPORTED_UPLOAD_FORMAT_LABEL,
  UPLOAD_ACCEPT,
} from '@/lib/uploadFormats';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const GUIDED_RESUME_LAST_COMPLETED_SESSION_KEY = 'jobfit_guided_resume_last_completed_session_id';

function readLastCompletedGuidedResumeSessionId() {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.sessionStorage.getItem(GUIDED_RESUME_LAST_COMPLETED_SESSION_KEY) || '';
}

function writeLastCompletedGuidedResumeSessionId(sessionId) {
  if (typeof window === 'undefined' || !sessionId) {
    return;
  }
  window.sessionStorage.setItem(GUIDED_RESUME_LAST_COMPLETED_SESSION_KEY, sessionId);
}

function clearLastCompletedGuidedResumeSessionId() {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.removeItem(GUIDED_RESUME_LAST_COMPLETED_SESSION_KEY);
}

function getCurrentSessionQuestions(session) {
  if (session?.status === 'completed') {
    return [];
  }

  if (session?.status === 'awaiting_strengthening_answers') {
    return session?.strengthening_questions || [];
  }

  return session?.initial_follow_up_questions || [];
}

function createAnswerDrafts(session) {
  const drafts = {};
  const questions = getCurrentSessionQuestions(session);

  questions.forEach((question) => {
    drafts[question.id] = '';
  });

  return drafts;
}

function formatCreditsSummary(remainingCredits) {
  if (!remainingCredits) {
    return null;
  }

  if (remainingCredits.is_unlimited) {
    return 'Unlimited guided resume credits available.';
  }

  const remaining = remainingCredits.remaining ?? 0;
  const limit = remainingCredits.limit ?? 0;
  const used = remainingCredits.used ?? 0;
  const bucketLabel = remainingCredits.bucket === 'free_lifetime'
    ? 'Lifetime credits'
    : 'Annual guided-resume credits';

  const periodLabel = remainingCredits.period_ends_at
    ? ` Credits reset on ${new Date(remainingCredits.period_ends_at).toLocaleDateString()}.`
    : '';

  return `${bucketLabel}: ${remaining} remaining out of ${limit}. ${used} used.${periodLabel}`;
}

function formatRemainingCreditsShort(remainingCredits) {
  if (!remainingCredits) {
    return 'Guided-resume credits are unavailable right now.';
  }

  if (remainingCredits.is_unlimited) {
    return 'You have unlimited guided-resume credits available.';
  }

  const remaining = remainingCredits.remaining ?? 0;
  return `You have ${remaining} guided-resume credit${remaining === 1 ? '' : 's'} remaining.`;
}

function CreditSummaryCard({ remainingCredits, effectivePlan }) {
  if (!remainingCredits) {
    return null;
  }

  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="font-medium text-foreground">Guided Resume Credits</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {formatCreditsSummary(remainingCredits)}
          </p>
        </div>
        <div className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
          {(remainingCredits.plan || effectivePlan || 'free').toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function InsightListCard({ title, items, tone = 'default' }) {
  if (!items?.length) {
    return null;
  }

  const toneClasses = tone === 'warning'
    ? 'border-warning/25 bg-warning/5'
    : 'border-border bg-surface';

  return (
    <div className={`rounded-xl border p-5 ${toneClasses}`}>
      <h3 className="font-medium text-foreground">{title}</h3>
      <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary/70 flex-shrink-0"></span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SavedResumeStatusNotice({ savedResumeStatus, savedResumeId }) {
  if (!savedResumeStatus && !savedResumeId) {
    return null;
  }

  const status = savedResumeStatus?.status || (savedResumeId ? 'saved' : 'warning');
  const message = savedResumeStatus?.message || 'This generated resume was saved to your saved resumes.';
  const isSuccess = status === 'saved';
  const isNeutral = status === 'skipped';

  const containerClasses = isSuccess
    ? 'border-primary/25 bg-primary/5'
    : isNeutral
      ? 'border-border bg-surface'
      : 'border-warning/30 bg-warning/5';
  const iconClasses = isSuccess ? 'text-primary' : isNeutral ? 'text-muted-foreground' : 'text-warning';
  const Icon = isSuccess ? CheckCircle2 : isNeutral ? Info : AlertTriangle;

  return (
    <div className={`rounded-xl border p-5 ${containerClasses}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${iconClasses}`} />
        <div>
          <p className="font-medium text-foreground">Saved Resume Status</p>
          <p className="mt-1 text-sm text-muted-foreground">{message}</p>
        </div>
      </div>
    </div>
  );
}

function GuidedResultStatusNotice({ resultView }) {
  if (!resultView?.noticeMessage || resultView.isComplete) {
    return null;
  }

  const isWarning = resultView.noticeTone === 'warning';
  const Icon = isWarning ? AlertTriangle : Info;

  return (
    <div className={`rounded-xl border p-5 ${isWarning ? 'border-warning/30 bg-warning/5' : 'border-border bg-surface'}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isWarning ? 'text-warning' : 'text-muted-foreground'}`} />
        <div>
          <p className="font-medium text-foreground">{resultView.noticeTitle}</p>
          <p className="mt-1 text-sm text-muted-foreground">{resultView.noticeMessage}</p>
        </div>
      </div>
    </div>
  );
}

export default function GuidedResumeCreatorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const contentTopRef = useRef(null);
  const guidedResumeCreatorEnabled = featureFlags.guidedResumeCreator;
  const effectivePlan = user?.effective_plan || user?.plan || 'free';

  const [backgroundText, setBackgroundText] = useState('');
  const [acknowledgeCreditConsumption, setAcknowledgeCreditConsumption] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [startingSession, setStartingSession] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [submittingFollowUp, setSubmittingFollowUp] = useState(false);
  const [generatingResume, setGeneratingResume] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [loadingExistingSession, setLoadingExistingSession] = useState(false);
  const [session, setSession] = useState(null);
  const [remainingCredits, setRemainingCredits] = useState(null);
  const [answerDrafts, setAnswerDrafts] = useState({});
  const [replacementNotice, setReplacementNotice] = useState('');
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictSession, setConflictSession] = useState(null);
  const [conflictRemainingCredits, setConflictRemainingCredits] = useState(null);
  const [pendingForceStartNew, setPendingForceStartNew] = useState(false);
  const [sessionPendingReplacement, setSessionPendingReplacement] = useState(null);
  const completedSessionId = searchParams.get('session');

  const creditSummaryText = useMemo(
    () => formatCreditsSummary(remainingCredits),
    [remainingCredits],
  );

  const scrollToContentTop = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const performScroll = () => {
      if (contentTopRef.current?.scrollIntoView) {
        contentTopRef.current.scrollIntoView({ block: 'start', behavior: 'auto' });
      } else {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(performScroll);
    });
  }, []);

  const applyLoadedSession = useCallback((loadedSession, loadedCredits) => {
    setSession(loadedSession || null);
    setRemainingCredits(loadedCredits || null);
    setBackgroundText(loadedSession?.background_text || '');
    setAnswerDrafts(createAnswerDrafts(loadedSession));
    setShowExportDropdown(false);
    setPendingForceStartNew(false);
    setSessionPendingReplacement(null);
    if (loadedSession?.status === 'completed' && loadedSession?.id) {
      writeLastCompletedGuidedResumeSessionId(loadedSession.id);
    }
  }, []);

  const loadActiveSession = useCallback(async () => {
    if (!guidedResumeCreatorEnabled) {
      navigate('/dashboard', { replace: true });
      return;
    }

    setLoadingPage(true);
    try {
      const data = await api.getGuidedResumeActiveSession();
      setRemainingCredits(data?.remaining_credits || null);

      if (data?.has_active_session && data?.session) {
        applyLoadedSession(data.session, data.remaining_credits || null);
        if (completedSessionId) {
          navigate('/guided-resume-creator', { replace: true });
        }
      } else {
        if (completedSessionId) {
          try {
            const completedSessionData = await api.getGuidedResumeSession(completedSessionId);
            if (completedSessionData?.session?.status === 'completed') {
              applyLoadedSession(
                completedSessionData.session,
                completedSessionData.remaining_credits || data?.remaining_credits || null,
              );
            } else {
              setSession(null);
              setPendingForceStartNew(false);
              setSessionPendingReplacement(null);
            }
          } catch (error) {
            clearLastCompletedGuidedResumeSessionId();
            setSession(null);
            setPendingForceStartNew(false);
            setSessionPendingReplacement(null);
          }
        } else {
          const lastCompletedSessionId = readLastCompletedGuidedResumeSessionId();
          if (lastCompletedSessionId) {
            try {
              const completedSessionData = await api.getGuidedResumeSession(lastCompletedSessionId);
              if (completedSessionData?.session?.status === 'completed') {
                applyLoadedSession(
                  completedSessionData.session,
                  completedSessionData.remaining_credits || data?.remaining_credits || null,
                );
                return;
              }
            } catch (error) {
              // Fall through to a clean start if the stored completed session is no longer available.
            }
            clearLastCompletedGuidedResumeSessionId();
          }
          setSession(null);
          setPendingForceStartNew(false);
          setSessionPendingReplacement(null);
        }
      }
    } catch (error) {
      const errorCode = getApiErrorCode(error);

      if (errorCode === 'guided_resume_creator_disabled') {
        navigate('/dashboard', { replace: true });
        return;
      }

      if (isEmailVerificationRequiredError(error)) {
        toast.error(getApiErrorMessage(error, 'Please verify your email before using this feature.'));
        navigate('/dashboard');
        return;
      }

      toast.error(getApiErrorMessage(error, 'Failed to load Guided Resume Creator'));
      navigate('/dashboard');
    } finally {
      setLoadingPage(false);
    }
  }, [applyLoadedSession, completedSessionId, guidedResumeCreatorEnabled, navigate]);

  useEffect(() => {
    loadActiveSession();
  }, [loadActiveSession]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isSupportedUploadFile(file)) {
      toast.error(getUnsupportedUploadFormatMessage());
      return;
    }

    const hadExistingText = Boolean(backgroundText.trim());
    setUploadingFile(true);
    try {
      const data = await api.uploadResume(file);
      setBackgroundText(data.text || '');
      setReplacementNotice(hadExistingText ? 'Previous text was replaced with the uploaded resume.' : '');
      toast.success(`Extracted text from ${data.filename}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to upload file'));
    } finally {
      setUploadingFile(false);
    }
  };

  const handleStartSession = async ({ forceStartNew = false } = {}) => {
    if (!backgroundText.trim()) {
      toast.error('Please enter or upload your background information');
      return;
    }

    if (!acknowledgeCreditConsumption) {
      toast.error('Please acknowledge that starting Guided Resume Creator uses a credit');
      return;
    }

    setStartingSession(true);
    try {
      const data = await api.startGuidedResumeSession({
        background_text: backgroundText,
        acknowledge_credit_consumption: true,
        force_start_new: forceStartNew || pendingForceStartNew,
      });

      applyLoadedSession(data.session, data.remaining_credits || null);
      navigate('/guided-resume-creator', { replace: true });
      scrollToContentTop();
      setReplacementNotice('');
      setConflictDialogOpen(false);
      setConflictSession(null);
      setConflictRemainingCredits(null);
    } catch (error) {
      const errorCode = getApiErrorCode(error);

      if (errorCode === 'guided_resume_session_in_progress') {
        const detail = error.response?.data?.detail || {};
        setConflictSession(detail.existing_session || null);
        setConflictRemainingCredits(detail.remaining_credits || null);
        setConflictDialogOpen(true);
        return;
      }

      if (errorCode === 'guided_resume_creator_disabled') {
        navigate('/dashboard', { replace: true });
        return;
      }

      if (isEmailVerificationRequiredError(error)) {
        toast.error(getApiErrorMessage(error, 'Please verify your email before using this feature.'));
        navigate('/dashboard');
        return;
      }

      toast.error(getApiErrorMessage(error, 'Failed to start Guided Resume Creator'));
    } finally {
      setStartingSession(false);
    }
  };

  const handlePrepareStartNewSession = useCallback(() => {
    const existingSession = session || conflictSession || sessionPendingReplacement;
    const replacementCredits = conflictRemainingCredits || remainingCredits;

    if (existingSession) {
      setSessionPendingReplacement(existingSession);
      if (!backgroundText.trim() && existingSession.background_text) {
        setBackgroundText(existingSession.background_text);
      }
    }

    if (replacementCredits) {
      setRemainingCredits(replacementCredits);
    }
    setPendingForceStartNew(true);
    setSession(null);
    setAnswerDrafts({});
    setAcknowledgeCreditConsumption(false);
    setReplacementNotice('');
    setConflictDialogOpen(false);
  }, [backgroundText, conflictRemainingCredits, conflictSession, remainingCredits, session, sessionPendingReplacement]);

  const handleRestoreExistingSession = useCallback(() => {
    if (!sessionPendingReplacement) {
      return;
    }

    applyLoadedSession(sessionPendingReplacement, remainingCredits);
    setConflictDialogOpen(false);
  }, [applyLoadedSession, remainingCredits, sessionPendingReplacement]);

  const handleResumeExistingSession = async () => {
    if (!conflictSession?.id) {
      setConflictDialogOpen(false);
      return;
    }

    if (session?.id === conflictSession.id) {
      setConflictDialogOpen(false);
      return;
    }

    setLoadingExistingSession(true);
    try {
      const data = await api.getGuidedResumeSession(conflictSession.id);
      applyLoadedSession(data.session, data.remaining_credits || null);
      navigate('/guided-resume-creator', { replace: true });
      scrollToContentTop();
      setConflictDialogOpen(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load the unfinished guided resume session'));
    } finally {
      setLoadingExistingSession(false);
    }
  };

  const handleSubmitInitialFollowUp = async () => {
    if (!session?.id || isStrengtheningStage || !canSubmitCurrentAnswers) {
      return;
    }

    setSubmittingFollowUp(true);
    try {
      const submittedAnswers = {};

      followUpQuestions.forEach((question) => {
        submittedAnswers[question.id] = answerDrafts[question.id] || '';
      });

      const data = await api.submitGuidedResumeFollowUp(session.id, {
        answers: submittedAnswers,
      });

      applyLoadedSession(data.session, data.remaining_credits || null);
      navigate('/guided-resume-creator', { replace: true });
      scrollToContentTop();
      toast.success('Final follow-up questions are ready.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to continue Guided Resume Creator'));
    } finally {
      setSubmittingFollowUp(false);
    }
  };

  const handleGenerateResume = async () => {
    if (!session?.id || !isStrengtheningStage || !canSubmitCurrentAnswers) {
      return;
    }

    setGeneratingResume(true);
    try {
      const submittedAnswers = {};

      strengtheningQuestions.forEach((question) => {
        submittedAnswers[question.id] = answerDrafts[question.id] || '';
      });

      const data = await api.generateGuidedResume(session.id, {
        answers: submittedAnswers,
      });

      applyLoadedSession(data.session, data.remaining_credits || null);
      navigate(`/guided-resume-creator?session=${data.session.id}`, { replace: true });
      scrollToContentTop();
      setReplacementNotice('');
      const generatedResultView = getGuidedResumeResultView(data.session);
      if (generatedResultView.isComplete) {
        toast.success(generatedResultView.toastMessage);
      } else {
        toast(generatedResultView.toastMessage || 'Guided resume result is ready for review.');
      }
    } catch (error) {
      if (isEmailVerificationRequiredError(error)) {
        toast.error(getApiErrorMessage(error, 'Please verify your email before using this feature.'));
        navigate('/dashboard');
        return;
      }

      toast.error(getApiErrorMessage(error, 'Failed to generate the guided resume'));
    } finally {
      setGeneratingResume(false);
    }
  };

  const handleExportResume = async (format) => {
    if (!session?.final_resume_text || !resultView.canExport) {
      toast(resultView.noticeMessage || 'This guided resume result is not available for export.');
      return;
    }

    setExportLoading(true);
    try {
      const guidedResumeExportData = buildGuidedResumeExportPayload(session);
      const data = await api.exportResume(
        session.final_resume_text,
        format,
        null,
        guidedResumeExportData,
        true,
        resultView.resultStatus,
      );
      const blob = await api.downloadProtectedFile(`/api/resume/download/${data.download_id}`);

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = data.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      setShowExportDropdown(false);
      toast.success(`${format.toUpperCase()} download started`);
    } catch (error) {
      if (isEmailVerificationRequiredError(error)) {
        toast.error(getApiErrorMessage(error, 'Please verify your email before using this feature.'));
        navigate('/dashboard');
      } else if (error?.response?.status === 403) {
        toast.error(getApiErrorMessage(error, 'Resume export requires Premium plan'));
        navigate('/pricing');
      } else {
        toast.error(getApiErrorMessage(error, 'Export failed'));
      }
    } finally {
      setExportLoading(false);
    }
  };

  const handleCopyResume = () => {
    if (!session?.final_resume_text || !resultView.canCopy) {
      toast(resultView.noticeMessage || 'This guided resume result is not available to copy.');
      return;
    }

    navigator.clipboard.writeText(session.final_resume_text);
    toast.success('Copied to clipboard!');
  };

  const handleStartFreshSession = () => {
    setSession(null);
    setBackgroundText('');
    setAnswerDrafts({});
    setAcknowledgeCreditConsumption(false);
    setReplacementNotice('');
    setConflictDialogOpen(false);
    setConflictSession(null);
    setConflictRemainingCredits(null);
    setPendingForceStartNew(false);
    setSessionPendingReplacement(null);
    setShowExportDropdown(false);
    clearLastCompletedGuidedResumeSessionId();
    navigate('/guided-resume-creator', { replace: true });
  };

  const followUpQuestions = session?.initial_follow_up_questions || [];
  const strengtheningQuestions = session?.strengthening_questions || [];
  const backgroundSummary = session?.background_summary || [];
  const scoreExplanation = session?.score_explanation || [];
  const biggestGaps = session?.biggest_gaps || [];
  const updatedResumeStrengths = session?.updated_resume_strengths || [];
  const remainingWeakSpots = session?.remaining_weak_spots || [];
  const postFollowUpScoreExplanation = session?.post_follow_up_score_explanation || [];
  const notesOrPossibleImprovements = session?.notes_or_possible_improvements || [];
  const savedResumeStatus = session?.saved_resume_status || null;
  const currentQuestions = getCurrentSessionQuestions(session);
  const isStrengtheningStage = session?.status === 'awaiting_strengthening_answers';
  const isCompletedStage = session?.status === 'completed';
  const resultView = getGuidedResumeResultView(session);
  const canSubmitCurrentAnswers = currentQuestions.some((question) => (answerDrafts[question.id] || '').trim());

  if (loadingPage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <MainMenu />
          <button
            onClick={() => navigate('/dashboard')}
            className="text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
              <span className="font-heading text-xl font-bold">Guided Resume Creator</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div ref={contentTopRef} aria-hidden="true" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="flex items-start gap-2 p-3 bg-surface border border-border rounded-lg text-sm">
            <Info className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
            <p className="text-muted-foreground">
              Guided Resume Creator builds or improves a general ATS-friendly resume from your background information.
              It is not for tailoring a resume to one specific job description.
            </p>
          </div>

          <CreditSummaryCard remainingCredits={remainingCredits} effectivePlan={effectivePlan} />

          {!session ? (
            <div className="bg-white border border-border rounded-xl p-6 shadow-soft">
              {pendingForceStartNew && (
                <div className="mb-5 rounded-xl border border-warning/30 bg-warning/5 p-4">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">
                          You are about to replace an unfinished guided session.
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          The old unfinished session will only be deleted after you successfully start a new one.
                          Starting new will use 1 guided-resume credit.
                        </p>
                        <p className="mt-2 text-sm text-muted-foreground">
                          {formatRemainingCreditsShort(remainingCredits)}
                        </p>
                      </div>
                    </div>
                    {sessionPendingReplacement && (
                      <button
                        type="button"
                        onClick={handleRestoreExistingSession}
                        className="inline-flex items-center justify-center rounded-lg border border-border bg-white px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-surface"
                        data-testid="guided-resume-return-existing-btn"
                      >
                        Resume Existing Session
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-heading text-lg font-semibold">
                    {pendingForceStartNew ? 'Start a New Guided Resume Session' : 'Start a Guided Resume Session'}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {pendingForceStartNew
                      ? 'Paste or upload updated background information, then start a new session when you are ready.'
                      : 'Paste a large background dump or upload a resume file and edit the extracted text before starting.'}
                  </p>
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFile || startingSession}
                  className="flex items-center gap-2 text-primary hover:text-primary-hover transition-colors text-sm"
                  data-testid="upload-btn"
                >
                  {uploadingFile ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Upload File
                </button>
              </div>

              <p className="text-xs text-muted-foreground mb-3">
                Supported: {SUPPORTED_UPLOAD_FORMAT_LABEL}. {LEGACY_DOC_GUIDANCE}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept={UPLOAD_ACCEPT}
                onChange={handleFileUpload}
                className="hidden"
                data-testid="file-input"
              />
              <textarea
                value={backgroundText}
                onChange={(e) => {
                  setBackgroundText(e.target.value);
                  setReplacementNotice('');
                }}
                placeholder="Paste your background information here. Include work history, education, volunteer work, projects, certifications, tools, skills, activities, accomplishments, and rough dates if you know them."
                className="w-full h-96 p-4 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none font-mono text-sm"
                data-testid="guided-resume-background-textarea"
              />
              {replacementNotice && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {replacementNotice}
                </p>
              )}

              <label className="mt-5 flex items-start gap-3 rounded-lg border border-border bg-surface p-4">
                <input
                  type="checkbox"
                  checked={acknowledgeCreditConsumption}
                  onChange={(e) => setAcknowledgeCreditConsumption(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  data-testid="guided-resume-acknowledgement-checkbox"
                />
                <span className="text-sm text-muted-foreground">
                  I understand that starting a new Guided Resume Creator session uses one guided-resume credit.
                </span>
              </label>

              {creditSummaryText && (
                <p className="mt-4 text-xs text-muted-foreground">
                  {creditSummaryText}
                </p>
              )}

              <button
                onClick={() => handleStartSession()}
                disabled={startingSession || uploadingFile || !backgroundText.trim() || !acknowledgeCreditConsumption}
                className="mt-6 w-full bg-gradient-to-r from-primary to-accent text-white py-4 rounded-xl font-medium hover:opacity-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="guided-resume-start-btn"
              >
                {startingSession ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {pendingForceStartNew ? 'Starting New Guided Resume Session...' : 'Starting Guided Resume Creator...'}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    {pendingForceStartNew ? 'Start New Guided Resume Session' : 'Start Guided Resume'}
                  </>
                )}
              </button>
            </div>
          ) : (
            <>
              <div className="bg-white border border-border rounded-xl p-6 shadow-soft">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h2 className="font-heading text-lg font-semibold">
                      {isCompletedStage
                        ? resultView.heading
                        : isStrengtheningStage
                          ? 'Final Follow-Up Questions'
                          : 'Session In Progress'}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {isCompletedStage
                        ? resultView.description
                        : isStrengtheningStage
                          ? 'These final questions will help close the last important gaps before generating the resume.'
                          : 'Answer the first follow-up questions below to unlock the final question set.'}
                    </p>
                    {!isCompletedStage && (
                      <button
                        type="button"
                        onClick={() => {
                          setConflictSession(session);
                          setConflictRemainingCredits(remainingCredits);
                          setConflictDialogOpen(true);
                        }}
                        className="mt-4 inline-flex items-center justify-center rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
                        data-testid="guided-resume-start-new-from-active-btn"
                      >
                        Start New Session
                      </button>
                    )}
                  </div>
                  <div className="rounded-xl bg-primary/10 px-4 py-3 text-center min-w-[160px]">
                    <div className="text-xs uppercase tracking-wide text-primary font-medium">
                      {isCompletedStage ? resultView.scoreLabel : 'Completeness Score'}
                    </div>
                    <div className="mt-1 text-3xl font-bold text-primary">
                      {isCompletedStage
                        ? (session.final_completeness_score ?? session.post_follow_up_completeness_score ?? session.initial_completeness_score)
                        : isStrengtheningStage
                          ? (session.post_follow_up_completeness_score ?? session.initial_completeness_score)
                          : session.initial_completeness_score}
                    </div>
                  </div>
                </div>
              </div>

              {!isStrengtheningStage && !isCompletedStage ? (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <InsightListCard title="Background Summary" items={backgroundSummary} />
                    <InsightListCard title="Biggest Gaps to Fill" items={biggestGaps} tone="warning" />
                  </div>

                  <InsightListCard title="Why This Score Looks Like It Does" items={scoreExplanation} />

                  {session.user_instructions && (
                    <div className="rounded-xl border border-border bg-surface p-5">
                      <h3 className="font-medium text-foreground">How to Answer These Questions</h3>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {session.user_instructions}
                      </p>
                    </div>
                  )}

                  <div className="space-y-4">
                    {followUpQuestions.map((question, index) => (
                      <div
                        key={question.id}
                        className="bg-white border border-border rounded-xl p-6 shadow-soft"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm flex-shrink-0">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-foreground">{question.question}</h3>
                            <textarea
                              value={answerDrafts[question.id] || ''}
                              onChange={(e) => {
                                setAnswerDrafts((currentDrafts) => ({
                                  ...currentDrafts,
                                  [question.id]: e.target.value,
                                }));
                              }}
                              placeholder="Your answer will be used to generate the final follow-up questions."
                              className="mt-4 w-full h-32 p-4 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none text-sm"
                              data-testid={`guided-resume-answer-${question.id}`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-white border border-border rounded-xl p-6 shadow-soft">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Continue to the final question set</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Submit your first-round answers to generate the last 5 targeted questions before resume generation.
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleSubmitInitialFollowUp}
                      disabled={submittingFollowUp || !canSubmitCurrentAnswers}
                      className="mt-5 w-full bg-primary text-white py-4 rounded-xl font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="guided-resume-continue-btn"
                    >
                      {submittingFollowUp ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Generating Final Questions...
                        </>
                      ) : (
                        <>
                          <FileText className="w-5 h-5" />
                          Continue to Final Questions
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : isStrengtheningStage ? (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <InsightListCard title="Updated Resume Strengths" items={updatedResumeStrengths} />
                    <InsightListCard title="Remaining Weak Spots" items={remainingWeakSpots} tone="warning" />
                  </div>

                  <InsightListCard
                    title="Why This Updated Score Looks Like It Does"
                    items={postFollowUpScoreExplanation}
                  />

                  {session.strengthening_user_instructions && (
                    <div className="rounded-xl border border-border bg-surface p-5">
                      <h3 className="font-medium text-foreground">How to Answer These Final Questions</h3>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {session.strengthening_user_instructions}
                      </p>
                    </div>
                  )}

                  <div className="space-y-4">
                    {strengtheningQuestions.map((question, index) => (
                      <div
                        key={question.id}
                        className="bg-white border border-border rounded-xl p-6 shadow-soft"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm flex-shrink-0">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-foreground">{question.question}</h3>
                            <textarea
                              value={answerDrafts[question.id] || ''}
                              onChange={(e) => {
                                setAnswerDrafts((currentDrafts) => ({
                                  ...currentDrafts,
                                  [question.id]: e.target.value,
                                }));
                              }}
                              placeholder='You can answer casually, use rough estimates, or write "not applicable" when needed.'
                              className="mt-4 w-full h-32 p-4 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none text-sm"
                              data-testid={`guided-resume-answer-${question.id}`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-white border border-border rounded-xl p-6 shadow-soft">
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Generate your final general resume</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Submit these last answers to create your completed ATS-friendly resume. Rough estimates are still okay when exact details are unavailable.
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleGenerateResume}
                      disabled={generatingResume || !canSubmitCurrentAnswers}
                      className="mt-5 w-full bg-primary text-white py-4 rounded-xl font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="guided-resume-generate-btn"
                    >
                      {generatingResume ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Generating Resume...
                        </>
                      ) : (
                        <>
                          <FileText className="w-5 h-5" />
                          Generate Resume
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <GuidedResultStatusNotice resultView={resultView} />

                  <SavedResumeStatusNotice
                    savedResumeStatus={savedResumeStatus}
                    savedResumeId={session.saved_resume_id}
                  />

                  <InsightListCard
                    title="Notes / Possible Improvements"
                    items={notesOrPossibleImprovements}
                    tone="warning"
                  />

                  <div className="bg-white border border-border rounded-xl p-6 shadow-soft">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <h3 className="font-heading text-lg font-semibold">{resultView.previewHeading}</h3>
                      <div className="flex flex-wrap gap-2">
                        {resultView.canCopy && (
                          <button
                            onClick={handleCopyResume}
                            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm"
                            data-testid="guided-resume-copy-btn"
                          >
                            <Copy className="w-4 h-4" />
                            {resultView.isComplete ? 'Copy Resume' : 'Copy Draft'}
                          </button>
                        )}
                        {resultView.canExport ? (
                          <div className="relative">
                            <button
                              onClick={() => setShowExportDropdown((current) => !current)}
                              disabled={exportLoading}
                              className="inline-flex items-center gap-2 text-primary hover:text-primary-hover transition-colors text-sm"
                              data-testid="guided-resume-export-btn"
                            >
                              {exportLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4" />
                              )}
                              Export
                              <ChevronDown className="w-4 h-4" />
                            </button>
                            {showExportDropdown && (
                              <div className="absolute right-0 top-full mt-1 bg-white border border-border rounded-lg shadow-medium py-1 z-10 min-w-[140px]">
                                <button
                                  onClick={() => handleExportResume('docx')}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-surface transition-colors"
                                  data-testid="guided-resume-export-docx"
                                >
                                  DOCX (Word)
                                </button>
                                <button
                                  onClick={() => handleExportResume('pdf')}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-surface transition-colors"
                                  data-testid="guided-resume-export-pdf"
                                >
                                  PDF
                                </button>
                                <button
                                  onClick={() => handleExportResume('txt')}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-surface transition-colors"
                                  data-testid="guided-resume-export-txt"
                                >
                                  TXT (Plain)
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                            <Info className="w-4 h-4" />
                            Export unavailable
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 text-xs text-muted-foreground">
                      {resultView.canExport ? (
                        <>
                          <span className="font-semibold text-foreground">Note:</span> Export uses the existing premium resume download flow.
                        </>
                      ) : (
                        resultView.noticeMessage || 'Export is unavailable for this guided resume result.'
                      )}
                    </div>

                    {resultView.showPreview ? (
                      <div
                        className="mt-4 rounded-lg border border-border bg-surface p-6 max-h-[700px] overflow-auto"
                        data-testid="guided-resume-generated-preview"
                      >
                        <pre className="whitespace-pre-wrap text-sm leading-7 text-foreground font-mono">
                          {session.final_resume_text}
                        </pre>
                      </div>
                    ) : (
                      <div className="mt-4 rounded-lg border border-warning/30 bg-warning/5 p-6 text-sm text-muted-foreground">
                        No exportable resume draft is available for this result.
                      </div>
                    )}
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={handleStartFreshSession}
                      className="flex-1 bg-white border border-border py-3 rounded-xl font-medium hover:bg-surface transition-all flex items-center justify-center gap-2"
                      data-testid="guided-resume-start-fresh-btn"
                    >
                      <RefreshCw className="w-5 h-5" />
                      Generate New / Start New Session
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </motion.div>
      </main>

      <Dialog open={conflictDialogOpen} onOpenChange={setConflictDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unfinished Guided Resume Session Found</DialogTitle>
            <DialogDescription>
              You already have an unfinished Guided Resume Creator session. You can resume it, or start a new one.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 text-sm">
            <div className="rounded-xl border border-warning/30 bg-warning/5 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Starting new will delete the old unfinished session and use 1 credit.</p>
                  <p className="mt-1 text-muted-foreground">
                    {formatRemainingCreditsShort(conflictRemainingCredits)}
                  </p>
                </div>
              </div>
            </div>

            {conflictSession && (
              <div className="rounded-xl border border-border bg-surface p-4">
                <p className="font-medium text-foreground">Existing session summary</p>
                <p className="mt-2 text-muted-foreground">
                  Started {new Date(conflictSession.created_at).toLocaleString()} with completeness score {conflictSession.initial_completeness_score}.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={handleResumeExistingSession}
              disabled={startingSession || loadingExistingSession}
              className="inline-flex items-center justify-center rounded-lg border border-border bg-white px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-surface disabled:opacity-60"
              data-testid="guided-resume-resume-existing-btn"
            >
              {loadingExistingSession ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Loading Session...
                </>
              ) : (
                'Resume Existing Session'
              )}
            </button>
            <button
              type="button"
              onClick={handlePrepareStartNewSession}
              disabled={startingSession || loadingExistingSession}
              className="inline-flex items-center justify-center rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-destructive/90 disabled:opacity-60"
              data-testid="guided-resume-force-start-btn"
            >
              Start New Session
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
